import React from 'react';

import cs from 'classnames';
import filter from 'lodash/filter';
import {Link} from 'react-router';

import {DocumentActionsButton} from 'app/components/general/DocumentActionsButton';
import {ItemRelationsConnection} from 'app/folders/components/ItemRelationsConnection';
import {
  StyledCompactItemHeaderDiv,
  StyledCompactItemHeaderSpan,
} from 'app/general/bootstrap/utils';
import {DocIsDraftBadge} from 'app/general/dashboards/DocIsDraftBadge';
import {DocIsTemplateBadge} from 'app/general/dashboards/DocIsTemplateBadge';
import {ItemLatestActivity} from 'app/general/dashboards/ItemLastActivity';
import {
  type BadgeRelation,
  ItemRelationsBadges,
} from 'app/general/dashboards/ItemRelationsBadges';
import {ItemRelationsListOfGenerics} from 'app/general/dashboards/ItemRelationsListOfGenerics';
import {ItemRelationsListOfTeams} from 'app/general/dashboards/ItemRelationsListOfTeams';
import {ItemRelationsLists} from 'app/general/dashboards/ItemRelationsLists';
import {DocumentPrivateStatusIcon} from 'app/general/doc-status';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {DocumentIconWithNotifications} from 'app/notifly/components/DocumentIconWithNotifications';
import {DashboardItemPresentMembers} from 'app/presence/components/DashboardItemPresentMembers';
import {StyledDocItem} from 'app/procedures/styles';
import {DocNeedsReviewBadge} from 'app/review/components/DocNeedReviewBadge';
import {getObjectName} from 'app/utils/fallbacks';

import {
  ConnectionFieldSerializedType,
  ContentType,
  DetailPolicySerializedType,
  ProcedureSerializedType,
} from 'sweet-types';

import type {NotificationType} from 'app/notifly/types';

interface Props {
  object: DetailPolicySerializedType;
  sortBy?: string;
  activeRelations?: Record<ContentType, boolean> | Record<string, boolean>;
  toggleRelation: (arg0: ContentType, arg1: boolean) => any;
  notifications?: Array<NotificationType>;
  refreshData: () => void;
  enableBulkSelect: boolean;
  isSelected: boolean;
  addToSelection: (policy: DetailPolicySerializedType) => unknown;
  removeFromSelection: (policy: DetailPolicySerializedType) => unknown;
}

export const CompactPolicyListItem = ({
  object,
  sortBy,
  activeRelations,
  toggleRelation,
  notifications,
  refreshData,
  enableBulkSelect,
  isSelected,
  addToSelection,
  removeFromSelection,
}: Props) => {
  function onSelectDoc(isChecked: boolean) {
    if (isChecked) {
      addToSelection(object);
    } else {
      removeFromSelection(object);
    }
  }

  return (
    <StyledDocItem
      className={cs(
        'row border-bottom py-2 d-flex align-items-center position-relative',
        {'opacity-overlay': object.approved_at == null}
      )}
    >
      <DashboardItemPresentMembers contentType="policy" objectId={object.id} />
      <StyledCompactItemHeaderDiv>
        {enableBulkSelect && (
          <CheckboxButton
            isChecked={isSelected}
            onClick={onSelectDoc}
            useRealCheckbox={true}
            className="mr-3 btn btn-sm btn-link"
          />
        )}
        <Link
          to={object.html_url}
          className="text-dark font-weight-bold d-block d-sm-inline"
        >
          <StyledCompactItemHeaderSpan>
            <DocumentIconWithNotifications
              contentType="policy"
              notifications={notifications}
            />{' '}
            {getObjectName(object, 'policy', true)}
          </StyledCompactItemHeaderSpan>
        </Link>
        <DocIsDraftBadge approved_at={object.approved_at} />
        <DocNeedsReviewBadge reviewDate={object.next_review_at} />
        <DocIsTemplateBadge template_at={object.template_at} />
      </StyledCompactItemHeaderDiv>
      <ItemRelationsBadges
        className="col-auto ml-auto ml-sm-0 order-lg-3"
        relations={relations}
        object={object}
        activeRelations={activeRelations}
        onToggle={toggleRelation}
      />

      <div className="col col-sm-6 col-lg-3 mr-auto order-lg-4">
        <ItemLatestActivity sortBy={sortBy} object={object} />
      </div>

      {object.private === false ? (
        <div className="col-auto order-lg-2 pr-0">
          <DocumentPrivateStatusIcon ct="policy" isPrivate={false} />
        </div>
      ) : null}

      <div className="col-auto order-lg-5">
        <DocumentActionsButton
          document={object}
          pageContext="dashboard"
          refreshData={refreshData}
        />
      </div>

      <div className="col-12 order-last">
        <ItemRelationsLists
          object={object}
          activeRelations={activeRelations}
          relations={relations}
        />
      </div>
    </StyledDocItem>
  );
};

const relations: Array<BadgeRelation> = [
  {
    ct: 'tag',
    getter: (p: DetailPolicySerializedType) => p.tags,
    Component: ItemRelationsListOfGenerics,
  },
  {
    ct: 'team',
    getter: (p: DetailPolicySerializedType) =>
      p.team_memberships.map((team) => team.team),
    Component: ItemRelationsListOfTeams,
  },
  {
    ct: 'folder',
    getter: (p: ProcedureSerializedType) => {
      const connections = p.connections;
      return filter(
        connections,
        (connection: ConnectionFieldSerializedType) => {
          return (
            connection.team == null &&
            connection.process == null &&
            connection.folder != null
          );
        }
      );
    },
    Component: ItemRelationsConnection,
  },
];
